import React, {useState, useEffect, useCallback} from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import '@sweetalert2/theme-dark/dark.min.css';
import {useTranslation} from "react-i18next";

function FarmManagement() {
    const [farms, setFarms] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        location: '',
        area_size: '',
        production_capacity: '',
        district: '',
        subdistrict: ''
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({
        total: 0,
        currentPage: 1,
        totalPages: 1,
    });
    const [pageSize] = useState(6); // Number of items per page
    const { t } = useTranslation();
    const [districts, setDistricts] = useState([]);
    const [subdistricts, setSubdistricts] = useState([]);
    const [filterDistrict, setFilterDistrict] = useState('');
    const [filterSubdistrict, setFilterSubdistrict] = useState('');
    const [filterSubdistricts, setFilterSubdistricts] = useState([]);
    const [ngos, setNgos] = useState([]);
    const [filterNgo, setFilterNgo] = useState('');



    const fetchFarms = useCallback(async (page = 1,searchTerm) => {
        const token = localStorage.getItem('token');
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: page,
                limit: pageSize,
            });

            if (searchTerm) params.append('search', searchTerm);
            if (filterDistrict) params.append('district', filterDistrict);
            if (filterSubdistrict) params.append('subdistrict', filterSubdistrict);
            if (filterNgo) params.append('ngo_id', filterNgo);


            const apiUrl = `${process.env.REACT_APP_BASE_URL}/api/farm/all?${params.toString()}`;
            const response = await axios.get(apiUrl, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setFarms(response.data.farms);
            setPagination({
                total: response.data.totalFarms,
                currentPage: response.data.currentPage,
                totalPages: response.data.totalPages,
            });
            setCurrentPage(response.data.currentPage);
            setLoading(false);
        } catch (error) {
            Swal.fire('Error', 'Failed to fetch farms.', 'error');
            setLoading(false);
        }
    }, [pageSize,filterDistrict,filterSubdistrict,filterNgo]);

    useEffect(() => {
        fetchFarms(currentPage,searchTerm);
    }, [currentPage,fetchFarms, searchTerm]);
    useEffect(() => {
        const fetchDistricts = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/districts`);
                setDistricts(response.data);

                // If district already selected (like in edit mode), populate its subdistricts
                if (formData.district) {
                    const selected = response.data.find(d => d.name === formData.district);
                    setSubdistricts(selected ? selected.subdistricts : []);
                }
            } catch (err) {
                console.error('Failed to fetch districts', err);
            }
        };
        fetchDistricts();
    }, [formData.district]);

    useEffect(() => {
        const fetchNgos = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/ngos`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setNgos(response.data);
            } catch (err) {
                console.error('Failed to fetch NGOs', err);
            }
        };
        fetchNgos();
    }, []);



    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'district') {
            const selectedDistrict = districts.find(d => d.name === value);
            setSubdistricts(selectedDistrict ? selectedDistrict.subdistricts : []);
            setFormData({
                ...formData,
                district: value,
                subdistrict: '', // reset subdistrict when district changes
            });
        } else {
            setFormData({
                ...formData,
                [name]: value,
            });
        }
    };

    const handlePageChange = (pageNumber) => {
        if (pageNumber > 0 && pageNumber <= pagination.totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        try {
            if (editing) {
                await axios.put(`${process.env.REACT_APP_BASE_URL}/api/farm/${editing._id}`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                Swal.fire('Success', 'Farm updated successfully', 'success');
            } else {
                await axios.post(`${process.env.REACT_APP_BASE_URL}/api/farm/create`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                Swal.fire('Success', 'Farm created successfully', 'success');
            }
            setFormData({
                name: '',
                location: '',
                area_size: '',
                production_capacity: '',
            });
            setEditing(null);
            setIsModalOpen(false);
            fetchFarms(currentPage,searchTerm);
        } catch (error) {
            Swal.fire('Error', 'Failed to save farm.', 'error');
        }
    };

    const handleEdit = (farm) => {
        setFormData({
            name: farm.name,
            location: farm.location,
            area_size: farm.area_size,
            production_capacity: farm.production_capacity,
            district: farm.district,
            subdistrict: farm.subdistrict
        });
        setEditing(farm);
        setIsModalOpen(true);
    };


    const openModal = () => {
        setFormData({
            name: '',
            location: '',
            area_size: '',
            production_capacity: '',
        });
        setEditing(null);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };



    const getPaginationButtons = () => {
        const { currentPage, totalPages } = pagination;
        const pageNumbers = [];

        let startPage = Math.max(currentPage - 2, 1);
        let endPage = Math.min(currentPage + 2, totalPages);

        if (startPage > 1) {
            pageNumbers.push(1);
            if (startPage > 2) pageNumbers.push('...');
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        if (endPage < totalPages - 1) pageNumbers.push('...');
        if (endPage < totalPages) pageNumbers.push(totalPages);

        return pageNumbers;
    };

    return (
        <div className="container mx-auto p-6">
            <h1 className="text-3xl font-bold mb-6">{t('farm_management')}</h1>
            <button
                onClick={openModal}
                className="bg-blue-500 text-white p-3 rounded mb-6 hover:bg-blue-600 transition duration-200"
            >
                {t('add_farm')}
            </button>

            <div className="bg-white shadow-md rounded-lg p-6">
                <h2 className="text-2xl font-semibold mb-4">{t('farm_list')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    {/* District Filter */}
                    <div>
                        <label className="block text-gray-700 mb-2">{t('Filter by NGO')}</label>
                        <select
                            value={filterNgo}
                            onChange={(e) => setFilterNgo(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                            <option value="">{t('All')} {t('NGOs')}</option>
                            {ngos.map((n) => (
                                <option key={n._id} value={n._id}>{n.name}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-gray-700 mb-2">
                            {t('Filter by District')}
                        </label>
                        <select
                            value={filterDistrict}
                            onChange={(e) => {
                                const selected = e.target.value;
                                setFilterDistrict(selected);
                                const found = districts.find(d => d.name === selected);
                                setFilterSubdistricts(found ? found.subdistricts : []);
                                setFilterSubdistrict('');
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                            <option value="">{t('All')} {t('Districts')}</option>
                            {districts.map((d) => (
                                <option key={d._id} value={d.name}>{t(d.name)}</option>
                            ))}
                        </select>
                    </div>

                    {/* Subdistrict Filter */}
                    <div>
                        <label className="block text-gray-700 mb-2">
                            {t('Filter by Subdistrict')}
                        </label>
                        <select
                            value={filterSubdistrict}
                            onChange={(e) => setFilterSubdistrict(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                            disabled={!filterDistrict}
                        >
                            <option value="">{t('All')} {t('Subdistricts')}</option>
                            {filterSubdistricts.map((s, i) => (
                                <option key={i} value={s}>{t(s)}</option>
                            ))}
                        </select>
                    </div>

                    {/* Search Farm */}
                    <div>
                        <label className="block text-gray-700 mb-2">
                            {t('search_farm_name')}
                        </label>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            placeholder={t('search_farm_name')}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                    </div>
                </div>

                {loading ? (
                    <div>Loading...</div>
                ) : farms.length > 0 ? (
                    <div>

                        <table className="min-w-full bg-white">
                            <thead>
                            <tr>
                                <th className="py-2 px-4 border-b">{t('name')}</th>
                                <th className="py-2 px-4 border-b">{t('NGO')}</th>
                                <th className="py-2 px-4 border-b">{t('location')}</th>
                                <th className="py-2 px-4 border-b">{t('area_size')}</th>
                                <th className="py-2 px-4 border-b">{t('number_of_pond')}</th>
                                <th className="py-2 px-4 border-b">{t('production_capacity')}</th>
                                <th className="py-2 px-4 border-b">{t('actions')}</th>
                            </tr>
                            </thead>
                            <tbody>
                            {farms.map((farm) => (
                                <tr key={farm._id}>
                                    <td className="py-2 px-4 border-b text-center">{farm.name}</td>
                                    <td className="py-2 px-4 border-b text-center">
                                        {farm.ngo_id ? (
                                            <span className="inline-block bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full">
                                                {farm.ngo_id.name}
                                            </span>
                                        ) : (
                                            <span className="text-gray-400 text-sm">None</span>
                                        )}
                                    </td>

                                    <td className="py-2 px-4 border-b text-center">{farm.location}</td>
                                    <td className="py-2 px-4 border-b text-center">{farm.area_size}</td>
                                    <td className="py-2 px-4 border-b text-center">{farm.number_of_pond}</td>
                                    <td className="py-2 px-4 border-b text-center">{farm.production_capacity}</td>
                                    <td className="py-2 px-4 border-b text-center">
                                        <button
                                            onClick={() => handleEdit(farm)}
                                            className="bg-yellow-500 text-white px-4 py-2 rounded mr-2 hover:bg-yellow-600"
                                        >
                                            {t('edit')}
                                        </button>
                                        {/*<button*/}
                                        {/*    onClick={() => handleDelete(farm._id)}*/}
                                        {/*    className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"*/}
                                        {/*>*/}
                                        {/*    Delete*/}
                                        {/*</button>*/}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                        {pagination.total > 0 && (
                            <div className="mt-6 flex justify-center items-center space-x-2">
                                <button
                                    onClick={() => handlePageChange(1)}
                                    className={`px-3 py-1 rounded ${pagination.currentPage === 1 ? 'bg-gray-300' : 'bg-gray-200 text-gray-800'}`}
                                    disabled={pagination.currentPage === 1}
                                >
                                    {t('first')}
                                </button>
                                <button
                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                    className={`px-3 py-1 rounded ${pagination.currentPage === 1 ? 'bg-gray-300' : 'bg-gray-200 text-gray-800'}`}
                                    disabled={pagination.currentPage === 1}
                                >
                                    {t('previous')}
                                </button>

                                {getPaginationButtons().map((page, index) =>
                                    page === '...' ? (
                                        <span key={index} className="px-3 py-1">...</span>
                                    ) : (
                                        <button
                                            key={index}
                                            onClick={() => handlePageChange(page)}
                                            className={`px-3 py-1 rounded ${
                                                pagination.currentPage === page ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-800'
                                            }`}
                                        >
                                            {page}
                                        </button>
                                    )
                                )}

                                <button
                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                    className={`px-3 py-1 rounded ${pagination.currentPage === pagination.totalPages ? 'bg-gray-300' : 'bg-gray-200 text-gray-800'}`}
                                    disabled={pagination.currentPage === pagination.totalPages}
                                >
                                    {t('next')}
                                </button>
                                <button
                                    onClick={() => handlePageChange(pagination.totalPages)}
                                    className={`px-3 py-1 rounded ${pagination.currentPage === pagination.totalPages ? 'bg-gray-300' : 'bg-gray-200 text-gray-800'}`}
                                    disabled={pagination.currentPage === pagination.totalPages}
                                >
                                    {t('last')}
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <p>No farms found.</p>
                )}
            </div>

            {isModalOpen && (
                <div className="fixed z-50 inset-0 overflow-y-auto">
                    <div
                        className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                        </div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                                    {editing ? t('edit_farm') : t('add_farm')}
                                </h3>
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-gray-700">{t('name')}</label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="w-full p-3 border border-gray-300 rounded mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-gray-700">{t('NGO')} ({t('optional')})</label>
                                        <select
                                            name="ngo_id"
                                            value={formData.ngo_id}
                                            onChange={handleChange}
                                            className="w-full p-3 border border-gray-300 rounded mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                        >
                                            <option value="">{t('Select')} {t('NGO')}</option>
                                            {ngos.map((ngo) => (
                                                <option key={ngo._id} value={ngo._id}>
                                                    {ngo.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-gray-700">{t('location')}</label>
                                        <input
                                            type="text"
                                            name="location"
                                            value={formData.location}
                                            onChange={handleChange}
                                            className="w-full p-3 border border-gray-300 rounded mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-gray-700">{t('District')}</label>
                                        <select
                                            name="district"
                                            value={formData.district}
                                            onChange={handleChange}
                                            className="w-full p-3 border border-gray-300 rounded mt-1"
                                            required
                                        >
                                            <option value="">{t('Select')} {t('District')}</option>
                                            {districts.map((d) => (
                                                <option key={d._id} value={d.name}>{t(d.name)}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-gray-700">{t('Subdistrict')}</label>
                                        <select
                                            name="subdistrict"
                                            value={formData.subdistrict}
                                            onChange={handleChange}
                                            className="w-full p-3 border border-gray-300 rounded mt-1"
                                            required
                                            disabled={!formData.district}
                                        >
                                            <option value="">{t('Select')} {t('Subdistrict')}</option>
                                            {subdistricts.map((s, i) => (
                                                <option key={i} value={s}>{t(s)}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-gray-700">{t('area_size')} (sq ft)</label>
                                        <input
                                            type="number"
                                            name="area_size"
                                            value={formData.area_size}
                                            onChange={handleChange}
                                            className="w-full p-3 border border-gray-300 rounded mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-gray-700">{t('production_capacity')}</label>
                                        <input
                                            type="text"
                                            name="production_capacity"
                                            value={formData.production_capacity}
                                            onChange={handleChange}
                                            className="w-full p-3 border border-gray-300 rounded mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                            required
                                        />
                                    </div>
                                    <div className="flex justify-end">
                                        <button
                                            type="button"
                                            onClick={closeModal}
                                            className="bg-gray-500 text-white p-3 rounded mr-4 hover:bg-gray-600 transition duration-200"
                                        >
                                            {t('cancel')}
                                        </button>
                                        <button
                                            type="submit"
                                            className="bg-blue-500 text-white p-3 rounded hover:bg-blue-600 transition duration-200"
                                        >
                                            {editing ? (t('update')) : (t('add'))}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default FarmManagement;